import { useCallback, useEffect, useRef } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useTrialAutoStartEnabled } from '../featureFlags';
import { trackFunnel, type FunnelData, type FunnelStage } from './funnel';
import { createLogger } from '../logging';
import { readTrialIntent, trialSourceFields } from '../trialIntent';

// Toggle in the browser console with: localStorage.debug = 'eh:funnel' (then reload).
const log = createLogger('funnel');

// Bridges the LD client to the pure trackFunnel adapter. Flushes immediately after
// track so events deliver promptly — important for stages that fire right before a
// navigation (e.g. the signup CTA → WorkOS redirect), which would otherwise be lost
// when the page unloads before the batch flush.
//
// The returned callback is memoised on the LD client identity. Consumers list it in
// `useEffect` deps (Welcome, Landing) to fire view events; an unstable identity would
// re-run those effects on every re-render and inflate the funnel counts. The identity
// only changes when the LD client resolves (null → client), which re-fires the view
// effect exactly once — the point at which events can actually be delivered.
//
// While the trial flow is released and a website trial intent is stored, every stage also carries its source — `product`,
// `intent` and the sanitised `utm_*` values, never the email — so the sign-up funnel from
// landing to organisation created can be split by campaign. A caller's own keys win.
export function useTrackFunnel(): (stage: FunnelStage, data?: FunnelData) => void {
  const ld = useLDClient();
  // Read through a ref so the flag settling never changes the callback's identity (see above).
  const trialFlow = useTrialAutoStartEnabled();
  const trialFlowRef = useRef(trialFlow);
  useEffect(() => { trialFlowRef.current = trialFlow; }, [trialFlow]);
  return useCallback(
    (stage: FunnelStage, data?: FunnelData) => {
      log('emit %s (ldClient present: %s)', stage, Boolean(ld));
      trackFunnel(
        stage,
        ld
          ? (k: string, d?: FunnelData) => {
              log('ld.track %s', k);
              if (d) ld.track(k, d);
              else ld.track(k);
              ld.flush?.();
            }
          : null,
        trialFlowRef.current ? withTrialSource(data) : data,
      );
    },
    [ld],
  );
}

function withTrialSource(data?: FunnelData): FunnelData | undefined {
  const intent = readTrialIntent();
  return intent ? { ...trialSourceFields(intent), ...data } : data;
}
